import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, ClipboardCheck, Scale, Droplets, Menu, Search, MapPin, AlertCircle, ChevronRight } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Input } from '../ui/input';

const daysSince = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);

const HiveRow = ({ hive, overdue, onPick }) => {
  const days = daysSince(hive.last_inspection);
  return (
    <button
      type="button"
      data-testid={`quick-inspect-hive-${hive.id}`}
      onClick={() => onPick(hive)}
      className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border border-stone-200 bg-white active:bg-amber-50 text-left"
    >
      <div className="min-w-0">
        <p className="font-semibold text-stone-800">Hive {hive.hive_number}{hive.name ? ` · ${hive.name}` : ''}</p>
        <p className={`text-xs ${overdue ? 'text-red-600 font-medium' : 'text-stone-500'}`}>
          {days === null ? 'Never inspected' : `Last inspected ${days} day${days === 1 ? '' : 's'} ago`}
        </p>
      </div>
      <ChevronRight className="h-5 w-5 text-stone-400 shrink-0" />
    </button>
  );
};

const HivePicker = ({ open, onOpenChange, hives, overdueIds }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const active = hives.filter(h => h.status !== 'inactive');
  const match = (h) => !q || `${h.hive_number} ${h.name || ''} ${h.yard || ''}`.toLowerCase().includes(q.toLowerCase());
  const due = active.filter(h => overdueIds.has(h.id) && match(h));
  const groups = (() => {
    const g = {};
    active.filter(match).forEach(h => { (g[h.yard || 'No apiary'] = g[h.yard || 'No apiary'] || []).push(h); });
    Object.values(g).forEach(list => list.sort((a, b) => String(a.hive_number).localeCompare(String(b.hive_number), undefined, { numeric: true })));
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  })();

  const pick = (hive) => {
    onOpenChange(false);
    navigate(`/hive/${hive.id}/new-inspection`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl p-4" data-testid="quick-inspect-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-amber-600" /> Which hive are you inspecting?</SheetTitle>
        </SheetHeader>
        <div className="relative my-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input data-testid="quick-inspect-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search hive number, name or apiary" className="pl-9" />
        </div>
        {due.length > 0 && (
          <div className="mb-4" data-testid="quick-inspect-due">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-2 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> Due for inspection ({due.length})</p>
            <div className="space-y-2">{due.map(h => <HiveRow key={h.id} hive={{ ...h, name: h.name, hive_number: `${h.hive_number}${h.yard ? ` (${h.yard})` : ''}` }} overdue onPick={pick} />)}</div>
          </div>
        )}
        {groups.map(([yard, list]) => (
          <div key={yard} className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-2 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {yard}</p>
            <div className="space-y-2">{list.map(h => <HiveRow key={h.id} hive={h} overdue={overdueIds.has(h.id)} onPick={pick} />)}</div>
          </div>
        ))}
        {groups.length === 0 && <p className="text-sm text-stone-400 text-center py-6">No hives found</p>}
      </SheetContent>
    </Sheet>
  );
};

export const MobileQuickActions = ({ hives = [], overdueIds = new Set(), onHarvest, onMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const view = new URLSearchParams(location.search).get('view');
  const items = [
    { key: 'home', icon: Home, label: 'Home', active: location.pathname === '/' && !view, onClick: () => navigate('/') },
    { key: 'inspect', icon: ClipboardCheck, label: 'Inspect', badge: overdueIds.size, onClick: () => setPickerOpen(true) },
    { key: 'weigh', icon: Scale, label: 'Weigh', active: view === 'bulk-weights', onClick: () => navigate('/?view=bulk-weights') },
    { key: 'harvest', icon: Droplets, label: 'Harvest', onClick: onHarvest },
    { key: 'menu', icon: Menu, label: 'Menu', onClick: onMenu }
  ];
  return (
    <>
      <nav data-testid="mobile-quick-actions" className="lg:hidden fixed bottom-0 left-0 right-0 z-[60] bg-white/95 backdrop-blur border-t border-stone-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-stretch h-16">
          {items.map(({ key, icon: Icon, label, active, badge, onClick }) => (
            <button
              key={key}
              type="button"
              data-testid={`quick-action-${key}`}
              onClick={onClick}
              className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors active:bg-amber-50 ${key === 'inspect' ? 'text-white' : active ? 'text-amber-600' : 'text-stone-600'}`}
            >
              {key === 'inspect' ? (
                <span className="flex items-center justify-center w-12 h-12 -mt-5 rounded-full bg-amber-500 shadow-lg ring-4 ring-white">
                  <Icon className="h-6 w-6" />
                </span>
              ) : (
                <Icon className={`h-5 w-5 ${active ? 'stroke-[2.5]' : ''}`} />
              )}
              <span className={`text-[11px] font-medium ${key === 'inspect' ? 'text-amber-700' : ''}`}>{label}</span>
              {badge > 0 && (
                <span data-testid="quick-inspect-badge" className="absolute top-0 right-[calc(50%-30px)] min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{badge}</span>
              )}
            </button>
          ))}
        </div>
      </nav>
      <HivePicker open={pickerOpen} onOpenChange={setPickerOpen} hives={hives} overdueIds={overdueIds} />
    </>
  );
};
